import { Grid, Typography, Container } from '@mui/material';
import Link from 'next/link';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import Image from 'next/image';
import VectorIcon from 'ui-component/HeaderSectionIcon';

interface ProductsSectionProps {
  handlePopoverClose: () => void;
}

const products = '/assets/images/products/header.svg';
const productsDark = '/assets/images/products/header-dark.svg';

const ProductsSection = ({ handlePopoverClose }: ProductsSectionProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const CustomLink = ({ link, text }: { link: string; text: string }) => (
    <Grid size={{ xs: 12 }}>
      <Grid
        container
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          '&:hover .text': {
            color: theme.palette.mode === 'dark' ? '#ED731D' : '#135799'
          }
        }}
      >
        <Grid>
          <VectorIcon />
        </Grid>
        <Grid>
          <Typography
            variant="body2"
            className="text"
            sx={{
              lineHeight: 1.4,
              textAlign: 'start',
              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
              marginLeft: 2,
              wordBreak: 'break-word'
            }}
          >
            <Link href={link} onClick={handlePopoverClose}>
              {formatMessage({ id: text })}
            </Link>
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );

  return (
    <Container sx={{ padding: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ md: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 16, md: 20 },
                  fontWeight: 600,
                  lineHeight: 1.4,
                  textAlign: 'start',
                  color: theme.palette.mode === 'dark' ? 'white' : '#135799'
                }}
              >
                {formatMessage({ id: 'PRODUCTS.SECTION.HEADER' })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                <Grid size={{ xs: 12 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.4,
                      textAlign: 'start',
                      color: theme.palette.mode === 'dark' ? 'white' : 'text.primary'
                    }}
                  >
                    {formatMessage({ id: 'PRODUCTS.SECTION.TEXT' })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          size={{ md: 3 }}
          sx={{
            paddingRight: '16px',
            borderRight: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.dark.light + 15 : theme.palette.grey[300]}`,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start'
          }}
        >
          <Image
            width={0}
            height={0}
            style={{ width: '100%', height: 'auto', maxHeight: 180 }}
            src={theme.palette.mode === 'dark' ? productsDark : products}
            alt={formatMessage({ id: 'PRODUCTS' })}
          />
        </Grid>

        <Grid
          size={{ md: 3 }}
          sx={{
            paddingRight: '16px',
            borderRight: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.dark.light + 15 : theme.palette.grey[300]}`,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start'
          }}
        >
          <Grid container spacing={2} pl={{ xs: 0, lg: 4 }}>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 16, md: 20 },
                  fontWeight: 600,
                  lineHeight: 1.4,
                  textAlign: 'start',
                  color: theme.palette.mode === 'dark' ? 'white' : '#135799'
                }}
              >
                {formatMessage({ id: 'SOFTWARE.PRODUCTS' })}
              </Typography>
            </Grid>
            <CustomLink link="/software-products" text="PRODUCTS.SECTION.SOFTWARE.LINK.1" />
            <CustomLink link="/software-products" text="PRODUCTS.SECTION.SOFTWARE.LINK.2" />
            <CustomLink link="/software-products" text="PRODUCTS.SECTION.SOFTWARE.LINK.3" />
            <CustomLink link="/software-products" text="PRODUCTS.SECTION.SOFTWARE.LINK.4" />
          </Grid>
        </Grid>
        <Grid size={{ md: 3 }}>
          <Grid container spacing={2} pl={{ xs: 0, lg: 4 }}>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: 16, md: 20 },
                  fontWeight: 600,
                  lineHeight: 1.4,
                  textAlign: 'start',
                  color: theme.palette.mode === 'dark' ? 'white' : '#135799'
                }}
              >
                {formatMessage({ id: 'HARDWARE.PRODUCTS' })}
              </Typography>
            </Grid>
            <CustomLink link="/hardware-products" text="PRODUCTS.SECTION.HARDWARE.LINK.1" />
            <CustomLink link="/hardware-products" text="PRODUCTS.SECTION.HARDWARE.LINK.2" />
            <CustomLink link="/hardware-products" text="PRODUCTS.SECTION.HARDWARE.LINK.3" />
            <CustomLink link="/hardware-products" text="PRODUCTS.SECTION.HARDWARE.LINK.4" />
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ProductsSection;
